import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Activity, AlertCircle, Building2, Check, Clock3, Database, RefreshCw, Server, Wifi, WifiOff } from 'lucide-react';
import { format } from 'date-fns';

interface Stats {
  companies: number;
  branches: { total: number; active: number; online: number; offline: number };
  requests: { total: number; active: number };
  executions: { last24h: number; failed24h: number; successRate: string };
}

interface ActivityItem {
  id: string;
  executedAt: string;
  success: boolean;
  statusCode?: number | null;
  executionTime?: number | null;
  branch: { name: string; company?: { name: string } };
  requestConfig: { name: string; endpointPath: string };
}

interface TopConsumer {
  queueName: string;
  branchId: string;
  branchName?: string;
  totalUploaded: string | number | bigint | null;
  totalDownloaded: string | number | bigint | null;
  totalTraffic: string | number | bigint | null;
}

interface BranchItem { id: string; name: string }
interface DashboardProps { demo?: boolean }

const ago = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const previewStats: Stats = {
  companies: 8,
  branches: { total: 24, active: 20, online: 18, offline: 2 },
  requests: { total: 64, active: 58 },
  executions: { last24h: 1248, failed24h: 6, successRate: '99,5' }
};
const previewActivity: ActivityItem[] = [
  { id: 'demo-1', executedAt: ago(3), success: true, statusCode: 200, executionTime: 184, branch: { name: 'Atlântico Sul', company: { name: 'Oceanic Transport' } }, requestConfig: { name: 'Filas simples', endpointPath: '/rest/queue/simple' } },
  { id: 'demo-2', executedAt: ago(11), success: true, statusCode: 200, executionTime: 236, branch: { name: 'Navegantes', company: { name: 'Bluewater Lines' } }, requestConfig: { name: 'Recursos do sistema', endpointPath: '/rest/system/resource' } },
  { id: 'demo-3', executedAt: ago(18), success: false, statusCode: 503, executionTime: 3000, branch: { name: 'Farol do Norte', company: { name: 'Oceanic Transport' } }, requestConfig: { name: 'Filas simples', endpointPath: '/rest/queue/simple' } },
  { id: 'demo-4', executedAt: ago(27), success: true, statusCode: 200, executionTime: 192, branch: { name: 'Costa Serena', company: { name: 'Maré Alta Serviços' } }, requestConfig: { name: 'Usuários Hotspot', endpointPath: '/rest/ip/hotspot/user' } },
  { id: 'demo-5', executedAt: ago(36), success: true, statusCode: 200, executionTime: 211, branch: { name: 'Estrela do Mar', company: { name: 'Bluewater Lines' } }, requestConfig: { name: 'Filas simples', endpointPath: '/rest/queue/simple' } }
];
const previewTopConsumers: TopConsumer[] = [
  { queueName: 'Crew Wi-Fi', branchId: 'v-1', branchName: 'Atlântico Sul', totalUploaded: 8_300_000_000, totalDownloaded: 44_800_000_000, totalTraffic: 53_100_000_000 },
  { queueName: 'Guest network', branchId: 'v-2', branchName: 'Navegantes', totalUploaded: 6_800_000_000, totalDownloaded: 38_200_000_000, totalTraffic: 45_000_000_000 },
  { queueName: 'Operations', branchId: 'v-3', branchName: 'Costa Serena', totalUploaded: 5_400_000_000, totalDownloaded: 27_900_000_000, totalTraffic: 33_300_000_000 },
  { queueName: 'Crew Wi-Fi', branchId: 'v-4', branchName: 'Estrela do Mar', totalUploaded: 3_900_000_000, totalDownloaded: 21_100_000_000, totalTraffic: 25_000_000_000 }
];
const previewBranches: BranchItem[] = [
  { id: 'v-1', name: 'Atlântico Sul' },
  { id: 'v-2', name: 'Navegantes' },
  { id: 'v-3', name: 'Costa Serena' },
  { id: 'v-4', name: 'Estrela do Mar' }
];

const toNumber = (value: string | number | bigint | null | undefined) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, index);
  return scaled.toFixed(scaled >= 10 || index === 0 ? 0 : 1) + ' ' + units[index];
};
const formatGB = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '0 GB';
  return value.toFixed(value % 1 === 0 ? 0 : 1) + ' GB';
};
const formatPercent = (value: string | undefined) => {
  const parsed = Number((value || '0').replace(',', '.'));
  return Number.isFinite(parsed) ? new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(parsed) : value || '0';
};
const GB_TO_BYTES = 1024 ** 3;

const Dashboard: React.FC<DashboardProps> = ({ demo = false }) => {
  const [stats, setStats] = useState<Stats | null>(demo ? previewStats : null);
  const [activity, setActivity] = useState<ActivityItem[]>(demo ? previewActivity : []);
  const [topConsumers, setTopConsumers] = useState<TopConsumer[]>(demo ? previewTopConsumers : []);
  const [branches, setBranches] = useState<BranchItem[]>(demo ? previewBranches : []);
  const [loading, setLoading] = useState(!demo);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(demo ? new Date() : null);
  const [calcPerUserGB, setCalcPerUserGB] = useState(1);
  const [calcUsers, setCalcUsers] = useState(12);
  const branchMap = useMemo(() => new Map(branches.map(branch => [branch.id, branch.name])), [branches]);
  const maxTraffic = useMemo(() => Math.max(...topConsumers.map(item => toNumber(item.totalTraffic)), 1), [topConsumers]);

  const loadDashboard = useCallback(async () => {
    setRefreshing(true);
    if (demo) {
      window.setTimeout(() => {
        setLastUpdated(new Date());
        setRefreshing(false);
      }, 450);
      return;
    }
    try {
      const response = await api.get('/dashboard/bootstrap', { params: { activityLimit: 15, topConsumersLimit: 10 } });
      const payload = response.data || {};
      setStats(payload.stats || null);
      setActivity(payload.activity || []);
      setTopConsumers(payload.topConsumers || []);
      setBranches(payload.branches || []);
      setLastUpdated(new Date());
    } catch {
      toast.error('Não foi possível carregar o painel. Tente atualizar novamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [demo]);

  useEffect(() => {
    if (!demo) loadDashboard();
  }, [demo, loadDashboard]);

  if (loading) {
    return (
      <div className="dashboard__loading" role="status" aria-live="polite">
        <span className="dashboard__loading-mark" aria-hidden="true" />
        <span>Carregando dados da operação…</span>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header dashboard__header">
        <div>
          <h1 className="page-header__title">Visão geral</h1>
          <p className="dashboard__intro">Acompanhe conectividade, coletas e tráfego das empresas.</p>
        </div>
        <div className="dashboard__header-actions">
          {lastUpdated && <span className="dashboard__updated"><Clock3 size={14} aria-hidden="true" />Atualizado às {format(lastUpdated, 'HH:mm:ss')}</span>}
          <button className="btn btn--primary" onClick={loadDashboard} disabled={refreshing} aria-busy={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Atualizando' : 'Atualizar'}
          </button>
        </div>
      </div>

      {demo && (
        <div className="dashboard__demo-note" role="note">
          <AlertCircle size={16} aria-hidden="true" />
          <span>Prévia visual · As informações desta tela são fictícias e servem apenas para demonstrar a interface.</span>
        </div>
      )}

      <section className="dashboard__metrics" aria-label="Resumo da operação">
        <div className="dashboard__metric">
          <div className="dashboard__metric-label"><Building2 size={15} aria-hidden="true" /> Empresas</div>
          <div className="dashboard__metric-value">{stats?.companies ?? 0}</div>
          <div className="dashboard__metric-caption">no escopo atual</div>
        </div>
        <div className="dashboard__metric dashboard__metric--online">
          <div className="dashboard__metric-label"><Wifi size={15} aria-hidden="true" /> Vessels online</div>
          <div className="dashboard__metric-value">{stats?.branches?.online ?? 0}<span> / {stats?.branches?.active ?? 0}</span></div>
          <div className="dashboard__metric-caption">vessels ativos</div>
        </div>
        <div className="dashboard__metric dashboard__metric--offline">
          <div className="dashboard__metric-label"><WifiOff size={15} aria-hidden="true" /> Vessels offline</div>
          <div className="dashboard__metric-value">{stats?.branches?.offline ?? 0}</div>
          <div className="dashboard__metric-caption">fora de comunicação</div>
        </div>
        <div className="dashboard__metric">
          <div className="dashboard__metric-label"><Server size={15} aria-hidden="true" /> Coletas ativas</div>
          <div className="dashboard__metric-value">{stats?.requests?.active ?? 0}<span> / {stats?.requests?.total ?? 0}</span></div>
          <div className="dashboard__metric-caption">configurações habilitadas</div>
        </div>
      </section>

      <section className="dashboard__collection" aria-label="Execuções de coleta nas últimas 24 horas">
        <div className="dashboard__collection-heading">
          <span className="dashboard__collection-mark" aria-hidden="true"><Activity size={17} /></span>
          <div><h2>Execução das coletas</h2><p>Resumo das últimas 24 horas</p></div>
        </div>
        <div className="dashboard__collection-stat">
          <span className="dashboard__collection-value">{stats?.executions?.last24h ?? 0}</span><span>execuções</span>
        </div>
        <div className="dashboard__collection-stat dashboard__collection-stat--success">
          <span className="dashboard__collection-value"><Check size={17} aria-hidden="true" /> {formatPercent(stats?.executions?.successRate)}%</span><span>taxa de sucesso</span>
        </div>
        <div className="dashboard__collection-stat dashboard__collection-stat--warning">
          <span className="dashboard__collection-value"><AlertCircle size={16} aria-hidden="true" /> {stats?.executions?.failed24h ?? 0}</span><span>falhas registradas</span>
        </div>
      </section>

      <div className="dashboard__work-grid">
        <section className="dashboard__activity" aria-labelledby="activity-title">
          <header className="dashboard__section-header">
            <div><h2 id="activity-title">Atividade recente</h2><p>Execuções mais recentes por empresa e vessel</p></div>
            <span className="dashboard__section-count">{activity.length} registros</span>
          </header>
          <div className="dashboard__table-scroll">
            <table className="data-table dashboard__activity-table">
              <thead><tr><th scope="col">Horário</th><th scope="col">Empresa / vessel</th><th scope="col">Coleta</th><th scope="col">Resultado</th></tr></thead>
              <tbody>
                {activity.length === 0 && <tr><td colSpan={4} className="dashboard__empty">Ainda não há execuções para exibir.</td></tr>}
                {activity.map(item => (
                  <tr key={item.id}>
                    <td className="dashboard__time-cell">{format(new Date(item.executedAt), 'dd/MM · HH:mm')}</td>
                    <td><span className="dashboard__company-cell">{item.branch.company?.name || '—'}</span><span className="dashboard__vessel-cell">{item.branch.name}</span></td>
                    <td><span className="dashboard__request-cell">{item.requestConfig.name}</span><span className="dashboard__endpoint-cell">{item.requestConfig.endpointPath}</span></td>
                    <td>
                      <span className={'dashboard__result ' + (item.success ? 'dashboard__result--success' : 'dashboard__result--failure')}>
                        {item.success ? <Check size={13} aria-hidden="true" /> : <AlertCircle size={13} aria-hidden="true" />}
                        {item.success ? 'Concluída' : 'Falha'}
                      </span>
                      {item.statusCode && <span className="dashboard__status-code">HTTP {item.statusCode}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="dashboard__activity-footer"><Database size={14} aria-hidden="true" /> Os dados refletem as execuções registradas pelo sistema.</footer>
        </section>

        <aside className="dashboard__side-column">
          <section className="dashboard__consumption" aria-labelledby="consumption-title">
            <header className="dashboard__section-header dashboard__section-header--compact">
              <div><h2 id="consumption-title">Maior consumo</h2><p>Tráfego acumulado em 24 horas</p></div>
            </header>
            {topConsumers.length === 0 ? (
              <p className="dashboard__empty dashboard__empty--side">Sem dados de consumo nas últimas 24 horas.</p>
            ) : (
              <ol className="dashboard__consumer-list">
                {topConsumers.slice(0, 5).map((item, index) => {
                  const traffic = toNumber(item.totalTraffic);
                  const width = Math.max(3, (traffic / maxTraffic) * 100);
                  return (
                    <li className="dashboard__consumer" key={item.queueName + '-' + item.branchId + '-' + index}>
                      <div className="dashboard__consumer-topline">
                        <span className="dashboard__consumer-rank">{String(index + 1).padStart(2, '0')}</span>
                        <div className="dashboard__consumer-name-wrap"><span className="dashboard__consumer-name">{item.queueName}</span><span className="dashboard__consumer-vessel">{item.branchName || branchMap.get(item.branchId) || item.branchId}</span></div>
                        <strong className="dashboard__consumer-value">{formatBytes(traffic)}</strong>
                      </div>
                      <div className="dashboard__traffic-track" role="img" aria-label={String(Math.round(width)) + ' por cento do maior tráfego'}><span style={{ transform: 'scaleX(' + width / 100 + ')' }} /></div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          <section className="dashboard__calculator" aria-labelledby="calculator-title">
            <header className="dashboard__section-header dashboard__section-header--compact">
              <div><h2 id="calculator-title">Estimativa de consumo</h2><p>Dimensionamento por usuário</p></div>
            </header>
            <div className="dashboard__calculator-fields">
              <label className="form-group">
                <span className="form-label">Consumo individual</span>
                <select value={calcPerUserGB} onChange={event => setCalcPerUserGB(Number(event.target.value))} className="form-input">
                  {[1, 1.5, 2, 2.5, 3, 3.5].map(value => <option key={value} value={value}>{value} GB por dia</option>)}
                </select>
              </label>
              <label className="form-group">
                <span className="form-label">Usuários</span>
                <input type="number" min={1} value={calcUsers} onChange={event => setCalcUsers(Math.max(1, Number(event.target.value) || 1))} className="form-input" />
              </label>
            </div>
            <div className="dashboard__estimate">
              <div><span>Total diário</span><strong>{formatGB(calcPerUserGB * calcUsers)}</strong></div>
              <div><span>Projeção em 30 dias</span><strong>{formatGB(calcPerUserGB * calcUsers * 30)}</strong></div>
            </div>
            <p className="dashboard__estimate-note">Referência: 1 GB = {formatBytes(GB_TO_BYTES)}.</p>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;
